// ----------------------------------------------------------------------
// <copyright file="Izenda.VirtualList.jsx" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { action } from 'mobx';
import { propTypes as ObservablePropTypes } from 'mobx-react';
import Utils from 'share/utils/Izenda.DomUtils';
import VirtualListItem from './Izenda.VirtualListItem';
import VirtualListUIStore from './Izenda.VirtualListUIStore';
import ScrollBox from 'share/components/controls/Izenda.ScrollBox';

import resizeDetector from 'share/utils/Izenda.SizeDetector';
import { observer } from 'mobx-react';
import classnames from 'classnames';

const DETAULT_COORDS_UPDATE_DEBOUNCE = 200;
const DEFAULT_SCROLL_THROTTLE = 150;
const DEFAULT_PAGE_TO_SCREEN_RATIO = 3;
import { invokeArgs, debounce, getOr, partial, forEach } from 'lodash/fp';

@observer
class VirtualHeight extends Component {
  render() {
    const {
      className,
      childRef,
      children,
    } = this.props;
    const classes = classnames('table-height', className);
    return (
      <div className={classes} ref={childRef}>
         {children}
      </div>
    );
  }
}


@observer
class BufferHeight extends Component {
  render() {
    const {
      className
    } = this.props;
    const bufferHeight = getOr(0, 'virtualStore.bufferHeight', this.props);
    return <div style={{ height: bufferHeight }} className={className}/>;
  }
}

@observer
export default class VirtualList extends Component {
  /**
   * @type {Array<{virtualItem, domNode}>}
   */
  _itemCoordsForUpdate = [];
  /**
   * @type {VirtualListUIStore}
   */
  virtualStore = null;
  viewDom = null;
  static propTypes = {
    dataSource: ObservablePropTypes.arrayOrObservableArray.isRequired,
    renderItem: PropTypes.func.isRequired,
    getChildItems: PropTypes.func,
    getItemKey: PropTypes.func,
    pageToScreenRatio: PropTypes.number,
    // Throttle milisecond of scrolling event
    scrollThrottle: PropTypes.number,
    // Indicate the virtual list has its own scroll bars
    ownScroll: PropTypes.bool
  };
  static defaultProps = {
    pageToScreenRatio: DEFAULT_PAGE_TO_SCREEN_RATIO,
    scrollThrottle: DEFAULT_SCROLL_THROTTLE,
    ownScroll: false
  };

  componentWillMount() {
    const { props } = this;
    const { pageToScreenRatio, dataSource } = props;

    this.virtualStore = new VirtualListUIStore(pageToScreenRatio, dataSource);
    invokeArgs('registerVirtualStore', [this.virtualStore], props);
  }

  componentDidMount() {
    const { dataSource, getChildItems } = this.props;
    this.virtualStore && this.virtualStore.initialize(dataSource, getChildItems);
    this.listen();
  }

  componentWillReceiveProps(nextProps) {
    const { dataSource } = nextProps;
    const { getChildItems } = this.props;

    this.virtualStore && this.virtualStore.initialize(dataSource, getChildItems);
  }

  componentWillUnmount() {
    this.updateAllCoords();
    this.onSyncCoordinatesWithDebounce && this.onSyncCoordinatesWithDebounce.cancel();
    this.unlisten && this.unlisten();
    if (this.virtualStore) {
      this.virtualStore.release();
      this.virtualStore = null;
    }
    this.unlisten = null;

  }

  /**
   * Call update coordinates as batch at once for several virtual items
   * and then clean queue.
   */
  @action updateAllCoords = () => {
    forEach(
      coordRecord => coordRecord && coordRecord.virtualItem.updateCoordsIfNeeded(coordRecord.domNode),
      this._itemCoordsForUpdate);
    this._itemCoordsForUpdate = [];
  };

  /**
   * Virtual List Item sync coords
   * @param {VirtualItem} virtualItem
   * @param {HTMLElement} domNode
   */
  updateAllCoordsDebounce = debounce(DETAULT_COORDS_UPDATE_DEBOUNCE, () => {
    this.updateAllCoords();
  });

  /**
   * Single virtual item sync coordinates handler. We're put item to queue immediately,
   * but call update coords function with debounce to prevent excessive renderings.
   * @param virtualItem
   * @param domNode
   */
  onSyncCoordinate = (virtualItem, domNode) => {
    this._itemCoordsForUpdate.push({virtualItem, domNode});
    this.updateAllCoordsDebounce();
  };

  render() {
    const props = this.props;
    const { ownScroll } = props;

    return ownScroll ? (
      <VirtualHeight className={props.className} childRef={this.setViewDom} virtualStore={this.virtualStore}>
        <ScrollBox onScroll={this.onScroll}>
          <BufferHeight virtualStore={this.virtualStore} className={props.bufferClassName}/>
          {this.renderList()}
        </ScrollBox>
      </VirtualHeight>
    ) : (
      <VirtualHeight className={props.className} childRef={this.setViewDom} virtualStore={this.virtualStore}>
        <BufferHeight virtualStore={this.virtualStore} className={props.bufferClassName}/>
        {this.renderList()}
      </VirtualHeight>
    );
  }

  renderList() {
    let itemIndex, itemsLength;
    const virtualStore = this.virtualStore;
    const virtualListItems = [];
    const virtualItemsOnScreen = this.virtualStore ? virtualStore.virtualItemsOnScreen : [];
    for (itemIndex = 0, itemsLength = virtualItemsOnScreen.length; itemIndex < itemsLength; itemIndex++) {
      let virtualItem = virtualItemsOnScreen[itemIndex];
      const renderItem = this.props.renderItem;
      const getItemKey = this.props.getItemKey;
      const dataItem = virtualItem.dataItem;
      const item = renderItem(dataItem, virtualItem.itemIndex);
      const key = getItemKey(dataItem) || virtualItem.itemIndex;

      const syncHandler = partial(this.onSyncCoordinate, [virtualItem]);
      virtualListItems.push(
        item && (
          <VirtualListItem
            key={key}
            item={virtualItem}
            onSyncCoordinates={syncHandler}>
            {item}
          </VirtualListItem>
        )
      );
    }

    return virtualListItems;
  }

  setViewDom = dom => {
    this.viewDom = dom;
  };

  getView() {
    return this.viewDom;
  }

  getBuffer() {
    return this.refs.buffer;
  }

  listen() {
    const virtual = this;
    const { ownScroll, scrollThrottle } = this.props;
    const view = this.getView();
    const scrollParent = Utils.findScrollableParent(view);
    if (scrollParent) {
      const viewport = scrollParent.parentNode;

      this.virtualStore && this.virtualStore.setViewportDimension(viewport.clientWidth, viewport.clientHeight);
      // Listen to virtual view resizing
      const throttleResize = debounce(scrollThrottle, function (elm) {
        virtual.onResize(elm);
      });
      resizeDetector.listenTo(viewport, throttleResize);

      if (!ownScroll) {
        this.onScroll(scrollParent);
        this.throttleScroll = debounce(scrollThrottle, scrollEvent => this.onScroll(scrollEvent.target), {
          maxWait: scrollThrottle * 3
        });
        scrollParent.addEventListener('scroll', this.throttleScroll, {
          capture: false,
          passive: true
        });
      }

      this.unlisten = function () {
        resizeDetector.removeListener(viewport, throttleResize);

        if (!ownScroll) {
          scrollParent.removeEventListener('scroll', this.throttleScroll, {
            capture: false,
            passive: true
          });
        }
      };
    }
  }

  onResize(elm) {
    const { clientWidth, clientHeight } = elm;
    if (this.virtualStore) {
      this.virtualStore.setViewportDimension(clientWidth, clientHeight);
    }
  }

  onScroll(viewNode) {
    let position = viewNode.scrollTop;
    const view = this.getView();

    if (!this.props.ownScroll) {
      const relativePosition = Utils.findRelativePositionToAcesstor(view, viewNode);
      position = -relativePosition.top;
    }

    this.virtualStore && this.virtualStore.calculateStartIndex(position);
  }
}



// WEBPACK FOOTER //
// ./ui/share/components/controls/Izenda.VirtualList.js